import React, { Component, CSSProperties, Fragment } from 'react'
import Downshift from 'downshift'
import compose from 'lodash/flowRight'
import styled, { css } from 'styled-components'

import {
  SelectComponentWrapper,
  SelectDropdownButton,
  Placeholder,
  Menu,
} from '~/views/components/ui/inputs/v2/Select/shared/components/selectElements'
import {
  ProviderProps,
  withDrawerLockingContext,
} from '~/views/components/WithDrawerLockingContext'

interface StyleCss extends CSSProperties {
  minHeight?: string
}

interface SingleSelectProps<Item> {
  onFocus: (event: React.FocusEvent<HTMLInputElement>) => void
  onBlur: (event: React.FocusEvent<HTMLInputElement>) => void
  name?: string
  placeholder: string
  renderValue: ({ value }: { value: string[] }) => void
  required?: boolean
  fullWidth?: boolean
  id: string
  disabled?: boolean
  drawerLockingContext: ProviderProps
  noPadding?: boolean
  borderless?: boolean
  style?: StyleCss
  children: any
  value: string
  onChange?: (selectedItem: Item) => void
  menuPosition: 'top'
  menuWrapper?: boolean
  floating?: boolean
  dataTestId?: string
}

interface MenuWrapperProps {
  floating?: boolean
}

const Styled = styled.div<MenuWrapperProps>`
  position: absolute;
  width: 160px;
  right: 0;

  & > div:first-child {
    display: grid;
    grid-template-columns: repeat(4, 40px);
    grid-template-rows: repeat(4, 40px);
    div {
      border-top: none;
    }
  }

  ${({ floating }) =>
    floating
      ? css`
          position: fixed;
          right: auto;
          margin-left: -42px;
          z-index: 99;
        `
      : ''};
`

class SingleSelect extends Component<SingleSelectProps<HTMLDivElement>> {
  override state = {
    focused: false,
  }

  onFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    const { onFocus } = this.props
    this.setState({ focused: true })
    if (onFocus) {
      onFocus(event)
    }
  }

  onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const { onBlur } = this.props
    this.setState({ focused: false })
    if (onBlur) {
      onBlur(event)
    }
  }

  onStateChange = (event: any) => {
    const { drawerLockingContext } = this.props
    if (event.isOpen) {
      drawerLockingContext && drawerLockingContext.disableBackdropClick()
    }
  }
  input: HTMLInputElement | null = null

  override render() {
    const {
      children,
      placeholder,
      renderValue,
      required,
      fullWidth,
      id,
      disabled,
      value,
      onChange,
      menuPosition,
      drawerLockingContext,
      borderless,
      noPadding,
      menuWrapper,
      floating,
      style,
      dataTestId,
      ...rest
    } = this.props

    const Wrapper = menuWrapper ? Styled : Fragment

    return (
      <Downshift
        itemToString={(selected) => (selected ? selected.name : '')}
        selectedItem={{ value: value || '' }}
        onChange={(selectedItem) => onChange?.(selectedItem.value)}
        onStateChange={this.onStateChange}
        {...rest}
      >
        {({
          getItemProps,
          getMenuProps,
          getRootProps,
          getToggleButtonProps,
          isOpen,
          selectedItem,
        }) => {
          return (
            <SelectComponentWrapper
              minHeight={style && style.minHeight}
              {...getRootProps({ refKey: 'innerRef' })}
            >
              <SelectDropdownButton
                data-testid={dataTestId}
                fullWidth={fullWidth}
                focused={this.state.focused}
                disabled={disabled}
                borderless={borderless}
                noPadding={noPadding}
                {...getToggleButtonProps({
                  onClick: () => this.input?.focus(),
                  disabled,
                })}
                style={style}
              >
                {!selectedItem.value && (
                  <Placeholder>{placeholder}</Placeholder>
                )}
                {typeof renderValue === 'function'
                  ? renderValue(selectedItem)
                  : React.Children?.map(children, (child) =>
                      (child as any)?.props?.value === selectedItem.value
                        ? child
                        : null,
                    )?.filter(Boolean)}
                <input
                  data-testid={
                    dataTestId ? `${dataTestId}-input` : 'downshift-input'
                  }
                  id={id}
                  name={rest.name}
                  required={required}
                  onFocus={this.onFocus}
                  onBlur={this.onBlur}
                  onKeyDown={(event) => {
                    drawerLockingContext &&
                      drawerLockingContext.handleKeyDown(
                        event as unknown as KeyboardEvent,
                      )
                  }}
                  disabled={disabled}
                  onChange={() => false}
                  value={selectedItem.value}
                  style={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0,
                    opacity: 0,
                    pointerEvents: 'none',
                    width: 1,
                  }}
                  ref={(node) => {
                    this.input = node
                  }}
                />
              </SelectDropdownButton>
              {isOpen && (
                <Wrapper {...(floating ? { floating: floating } : undefined)}>
                  <Menu
                    menuPosition={menuPosition}
                    {...getMenuProps({ open: isOpen })}
                  >
                    {React.Children.map(children, (child, index) => {
                      return React.cloneElement(child, {
                        ...getItemProps({
                          item: { value: child?.props.value },
                          index,
                          isSelected: selectedItem === child?.props.value,
                          disabled: child?.props.disabled,
                        }),
                      })
                    })}
                  </Menu>
                </Wrapper>
              )}
            </SelectComponentWrapper>
          )
        }}
      </Downshift>
    )
  }
}

export default compose(withDrawerLockingContext)(SingleSelect)
